import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Box, Button, Card, Heading, Spinner } from '@hyphen/hyphen-components';

import mark from '../../assets/static/logo.svg';
import hyphenSymbol from '@hyphen/hyphen-design-tokens/build/assets/images/hyphen-symbol-rgb.svg';
// The design system's own icons, imported as files. The library's Icon reads
// them through a CommonJS module whose default export bun's bundler does not
// unwrap, so every name looked missing and it rendered "???" in their place.
import checkIcon from '@hyphen/hyphen-design-tokens/build/assets/icons/check.svg';
import downloadIcon from '@hyphen/hyphen-design-tokens/build/assets/icons/download.svg';
import lockIcon from '@hyphen/hyphen-design-tokens/build/assets/icons/lock.svg';
import mailIcon from '@hyphen/hyphen-design-tokens/build/assets/icons/mail.svg';
import cloudflareLogo from '../../assets/static/logos/cloudflare.svg';
import googleCloudLogo from '../../assets/static/logos/google-cloud.svg';
import incidentIoLogo from '../../assets/static/logos/incident-io.svg';
import newRelicLogo from '../../assets/static/logos/new-relic.svg';
import { portalApi, portalDownloadURL, portalSignInURL } from '../lib/api.js';

/**
 * The public trust portal, modelled on Ory's (trust.ory.com): what Hyphen has
 * in place, readable by anyone, and the documents behind it for customers whose
 * organization has been let in.
 *
 * Customers sign in with their Hyphen account. That is a different sign-in from
 * the admin's and a different session: nothing here reads the admin's, and an
 * admin signed in to /admin is signed out here until they sign in as a customer.
 */

// Where to reach Hyphen about any of this. The address the privacy policy
// gives, until there is a dedicated security one.
const CONTACT = 'help@hyphen.ai';
const PRIVACY_POLICY = 'https://hyphen.ai/privacy-policy';

// Static, in code: they change when an examination does, which is a release.
const COMPLIANCE = [
  { name: 'SOC 2 Type 1', detail: 'Examined as of June 30, 2026', status: 'Complete', color: 'green' },
  {
    name: 'SOC 2 Type 2',
    detail: 'Observation period September 1 – November 30, 2026',
    status: 'In progress',
    color: 'blue',
  },
];

// The services that process customer data on Hyphen's behalf. Static, in code,
// like the compliance lines. Where each runs is left out until it is confirmed.
// A service without a logo here is shown with its initial.
const SUBPROCESSORS = [
  { name: 'Google Cloud Platform', purpose: 'Hosting: compute, storage and networking', logo: googleCloudLogo },
  { name: 'MongoDB Atlas', purpose: 'Managed database' },
  { name: 'Cloudflare', purpose: 'DNS, content delivery, and protection from attacks', logo: cloudflareLogo },
  { name: 'Ory', purpose: 'Customer accounts and sign-in' },
  { name: 'New Relic', purpose: 'Application monitoring', logo: newRelicLogo },
  { name: 'incident.io', purpose: 'Incident response', logo: incidentIoLogo },
];

// The public website's footer, so the portal reads as part of hyphen.ai. Its
// columns mirror footerColumns in public-website's atrium/nav-data.ts, as
// absolute links; "SDKs", which the site marks pending, and llms.txt are left
// out. Keep the two in step when the site's changes.
const SITE = 'https://hyphen.ai';
const FOOTER_COLUMNS = [
  {
    title: 'Platform',
    links: [
      { label: 'Deploy', href: `${SITE}/deploy` },
      { label: 'Agent', href: `${SITE}/agent` },
      { label: 'ENV', href: `${SITE}/env` },
      { label: 'Toggle', href: `${SITE}/toggle` },
    ],
  },
  {
    title: 'Solutions',
    links: [
      { label: 'Multi-Cloud Deploy', href: `${SITE}/multi-cloud` },
      { label: 'Managed SaaS', href: `${SITE}/managed-saas` },
      { label: 'Managed Ops', href: `${SITE}/managed-ops` },
      { label: 'Managed Kubernetes', href: `${SITE}/managed-kubernetes` },
      { label: 'CMMC Compliance', href: `${SITE}/cmmc-compliance` },
      { label: 'SOC 2 Type 2', href: `${SITE}/soc-2-compliance` },
    ],
  },
  {
    title: 'Developers',
    links: [
      { label: 'Documentation', href: `${SITE}/docs` },
      { label: 'API reference', href: `${SITE}/docs/api` },
      { label: 'CLI', href: `${SITE}/cli` },
      { label: 'MCP server', href: `${SITE}/mcp` },
      { label: 'Changelog', href: `${SITE}/docs/changelog` },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'Privacy Policy', href: `${SITE}/privacy-policy` },
      { label: 'Terms of Service', href: `${SITE}/terms-of-service` },
      { label: 'Service Level Agreement', href: `${SITE}/sla` },
      { label: 'Status', href: 'https://status.hyphen.ai' },
    ],
  },
];

// How many controls a category shows before "show all".
const CONTROLS_SHOWN = 4;

const ICONS = { check: checkIcon, download: downloadIcon, lock: lockIcon, mail: mailIcon };

/**
 * An icon drawn as a mask over the text colour, so it follows the colour of
 * whatever it sits in as the library's icons do. An <img> would draw them black
 * whatever the text around them.
 */
function Icon({ name, className = '' }) {
  const url = `url(${ICONS[name]})`;
  return (
    <span className={`portal-icon ${className}`} aria-hidden="true" style={{ maskImage: url, WebkitMaskImage: url }} />
  );
}

export function Portal() {
  const [overview, setOverview] = useState(null);
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);

  const loadSession = useCallback(() => portalApi.get('/portal/session').then(setSession), []);

  useEffect(() => {
    Promise.all([portalApi.get('/portal/overview').then(setOverview), loadSession()])
      .catch((err) => setError(err.message));
  }, [loadSession]);

  async function signOut() {
    await portalApi.delete('/portal/session');
    setSession((current) => ({ signed_in: false, sign_in_enabled: current?.sign_in_enabled }));
  }

  return (
    <Box minHeight="100vh" background="secondary">
      <Header session={session} onSignOut={signOut} />
      <Intro />
      <Box as="main" className="content-column" padding="3xl 0 5xl" gap="2xl">
        {error && <Alert variant="danger">The trust portal could not be loaded. Try again shortly.</Alert>}
        {!error && (!overview || !session) && <Spinner />}
        {overview && session && (
          <>
            <Access session={session} onChanged={loadSession} onError={setError} />
            {/* Plain elements for the grids: Box's own flex classes win over one. */}
            <div className="portal-columns">
              <div className="portal-stack">
                <Compliance />
                <Documents documents={overview.documents} session={session} />
                <Subprocessors />
              </div>
              <Controls categories={overview.controls} />
            </div>
          </>
        )}
      </Box>
      <Footer />
    </Box>
  );
}

function Footer() {
  return (
    <Box as="footer" background="primary" borderWidth="sm 0 0 0" borderColor="subtle">
      <Box className="content-column" padding="4xl 0 2xl" gap="3xl">
        <div className="portal-footer-columns">
          <Box gap="lg">
            <a href={SITE} className="portal-footer-brand">
              <img src={hyphenSymbol} alt="" aria-hidden="true" width="24" height="21" />
              hyphen
            </a>
            <Box as="p" fontSize="sm" color="secondary" style={{ maxWidth: '16rem' }}>
              The operations agent for what your teams ship.
            </Box>
          </Box>
          {FOOTER_COLUMNS.map((column) => (
            <Box key={column.title} gap="md">
              <Box as="span" fontSize="xs" fontWeight="semibold" color="secondary" className="portal-eyebrow">
                {column.title}
              </Box>
              {column.links.map((link) => (
                <a key={link.label} href={link.href} className="portal-footer-link">{link.label}</a>
              ))}
            </Box>
          ))}
        </div>
        <Box borderWidth="sm 0 0 0" borderColor="subtle" padding="xl 0 0" fontSize="sm" color="secondary">
          &copy; {new Date().getFullYear()} Hyphen, Inc.
        </Box>
      </Box>
    </Box>
  );
}

function Header({ session, onSignOut }) {
  return (
    <Box as="header" background="primary" borderWidth="0 0 sm 0" borderColor="subtle">
      <Box className="content-column" direction="row" alignItems="center" gap="xl" padding="lg 0">
        <span className="app-nav__brand">
          <img src={mark} alt="" aria-hidden="true" width="24" height="24" />
          Hyphen Trust
        </span>
        <Box direction="row" alignItems="center" gap="lg" style={{ marginLeft: 'auto' }}>
          {session?.signed_in && (
            <>
              <Box as="span" fontSize="sm" color="secondary">{session.email}</Box>
              <Button variant="secondary" size="sm" onClick={onSignOut}>Sign out</Button>
            </>
          )}
          {session && !session.signed_in && session.sign_in_enabled && (
            <Button asChild size="sm">
              <a href={portalSignInURL()}>Sign in for documents</a>
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}

/** The page's opening, across its whole width, as Ory's is. */
function Intro() {
  return (
    <Box background="primary" borderWidth="0 0 sm 0" borderColor="subtle">
      <Box className="content-column" padding="4xl 0" gap="xl">
        <Box gap="lg">
          <Heading as="h1" size="2xl">Security and compliance at Hyphen</Heading>
          <Box as="p" fontSize="lg" color="secondary">
            The controls Hyphen has in place to protect its customers and their data, and the reports and
            policies behind them. Documents are available to customers whose organization has been given
            access: sign in with your Hyphen account to download them, or to ask for access.
          </Box>
        </Box>
        <Box direction="row" gap="2xl" fontSize="sm" wrap>
          <a className="portal-link" href={`mailto:${CONTACT}`}>
            <Icon name="mail" /> {CONTACT}
          </a>
          <a className="portal-link" href={PRIVACY_POLICY} target="_blank" rel="noreferrer">Privacy policy</a>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Where a signed-in customer stands: let in, waiting, or able to ask. Nothing
 * for a visitor who has not signed in, beyond the button in the header.
 */
function Access({ session, onChanged, onError }) {
  const [asking, setAsking] = useState(null);

  if (!session.signed_in || session.can_download) return null;

  async function ask(organizationID) {
    setAsking(organizationID);
    try {
      await portalApi.post('/portal/access-requests', { organization_id: organizationID });
      await onChanged();
    } catch (err) {
      onError(err.message);
    } finally {
      setAsking(null);
    }
  }

  const organizations = session.organizations ?? [];
  return (
    <Card>
      <Card.Header
        title="Access to the documents"
        description={organizations.length === 0
          ? <>Your Hyphen account isn't a member of any organization, and access is given to organizations. If you expected to see one, contact <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.</>
          : "None of your organizations has access yet. Ask for it here, and we'll let you know once it's been given."}
      />
      {organizations.length > 0 && (
        <Card.Section gap="md">
          {organizations.map((org) => (
            <Box key={org.id} direction="row" alignItems="center" gap="lg">
              <Box as="span" fontWeight="medium">{org.name}</Box>
              {org.requested ? (
                <Badge variant="soft" color="yellow">Requested</Badge>
              ) : (
                <Button size="sm" variant="secondary" isLoading={asking === org.id}
                        isDisabled={Boolean(asking)} onClick={() => ask(org.id)}>
                  Request access
                </Button>
              )}
            </Box>
          ))}
        </Card.Section>
      )}
    </Card>
  );
}

function Compliance() {
  return (
    <Card>
      <Card.Header title="Compliance" />
      <Card.Section gap="lg">
        {COMPLIANCE.map((item) => (
          <Box key={item.name} direction="row" alignItems="center" gap="lg">
            <span className={`portal-seal portal-seal--${item.color}`} aria-hidden="true">SOC 2</span>
            <Box gap="2xs" flex="auto">
              <Box as="span" fontWeight="semibold">{item.name}</Box>
              <Box as="span" fontSize="sm" color="secondary">{item.detail}</Box>
            </Box>
            <Box flex="none" style={{ whiteSpace: 'nowrap' }}>
              <Badge variant="soft" color={item.color}>{item.status}</Badge>
            </Box>
          </Box>
        ))}
      </Card.Section>
    </Card>
  );
}

/**
 * Every document's title, for anyone. The file only for a customer whose
 * organization has access; everyone else sees what exists and a lock.
 */
function Documents({ documents, session }) {
  const sections = [...new Set(documents.map((d) => d.section))];
  return (
    <Card>
      <Card.Header
        title="Documents"
        description={session.can_download ? 'Your organization has access.' : 'Available to customers whose organization has access.'}
      />
      <Card.Section gap="lg">
        {documents.length === 0 && <Box as="p" color="secondary">No documents are published yet.</Box>}
        {sections.map((section) => (
          <Box key={section} gap="sm">
            <Box as="span" fontSize="xs" fontWeight="semibold" color="secondary" className="portal-eyebrow">{section}</Box>
            {documents.filter((d) => d.section === section).map((document) =>
              session.can_download ? (
                <a key={document.id} className="portal-link" href={portalDownloadURL(document.id)}>
                  <Icon name="download" /> {document.title}
                </a>
              ) : (
                <Box key={document.id} as="span" direction="row" alignItems="center" gap="sm" color="secondary">
                  <Icon name="lock" /> {document.title}
                </Box>
              ),
            )}
          </Box>
        ))}
      </Card.Section>
    </Card>
  );
}

function Controls({ categories }) {
  const total = categories.reduce((n, c) => n + c.controls.length, 0);
  return (
    <Card>
      <Card.Header
        title="Controls"
        description={total ? `${total} controls in place across ${categories.length} areas` : undefined}
      />
      <Card.Section>
        {categories.length === 0 && <Box as="p" color="secondary">Controls will be listed here.</Box>}
        <div className="portal-control-grid">
          {categories.map((category) => <Category key={category.category} category={category} />)}
        </div>
      </Card.Section>
    </Card>
  );
}

function Category({ category }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? category.controls : category.controls.slice(0, CONTROLS_SHOWN);
  const hidden = category.controls.length - shown.length;
  return (
    <Box padding="xl" radius="md" borderWidth="sm" borderColor="subtle" gap="md">
      <Box direction="row" alignItems="center" gap="sm">
        <Box as="span" fontWeight="semibold">{category.category}</Box>
        <Badge variant="soft" color="grey" size="sm">{category.controls.length}</Badge>
      </Box>
      <div className="portal-category-list">
        {shown.map((control) => (
          <Box key={control.title} direction="row" gap="sm">
            <Icon name="check" className="portal-icon--success" />
            <Box gap="2xs">
              <Box as="span" fontSize="sm" fontWeight="medium">{control.title}</Box>
              {control.description && <Box as="span" fontSize="sm" color="secondary">{control.description}</Box>}
            </Box>
          </Box>
        ))}
      </div>
      {(hidden > 0 || expanded) && (
        <Box alignItems="flex-start">
          <Button variant="link" size="sm" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show fewer' : `Show ${hidden} more`}
          </Button>
        </Box>
      )}
    </Box>
  );
}

function Subprocessors() {
  return (
    <Card>
      <Card.Header title="Subprocessors" description="The services that process customer data on Hyphen's behalf." />
      <Card.Section>
        <div className="portal-list">
          {SUBPROCESSORS.map((s) => (
            <Box key={s.name} direction="row" gap="lg" alignItems="center">
              <span className="portal-logo">
                {s.logo ? <img src={s.logo} alt="" /> : <span aria-hidden="true">{s.name[0]}</span>}
              </span>
              <Box gap="2xs">
                <Box as="span" fontWeight="semibold">{s.name}</Box>
                <Box as="span" fontSize="sm" color="secondary">{s.purpose}</Box>
              </Box>
            </Box>
          ))}
        </div>
      </Card.Section>
    </Card>
  );
}
